import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { noticeForError } from '@backrs_ui/grow-ui/src/components/InstitutionWrite/noticeForError';
import { WriteDialog } from '@backrs_ui/grow-ui/src/components/InstitutionWrite/WriteDialog';
import { isSubmittableCohortName } from '@backrs_ui/grow-ui/src/redux/institutionAdmin';

import { Box, Button, type SxProps, TextField, type Theme, Typography } from '@mui/material';

const { renameDialog: strings } = appStrings.modules.rostersAdmin;

type RenameCohortDialogProps = Readonly<{
  open: boolean;
  onClose: () => void;
  /** The cohort being renamed and the roster it sits in, both named in the title. */
  cohortName: string;
  rosterName: string;
  name: string;
  onNameChange: (next: string) => void;
  isSubmitting: boolean;
  isWriteInFlight: boolean;
  isError: boolean;
  errorMessage?: string | null;
  onSubmit: () => void;
}>;

export const RenameCohortDialog = ({
  open,
  onClose,
  cohortName,
  rosterName,
  name,
  onNameChange,
  isSubmitting,
  isWriteInFlight,
  isError,
  errorMessage,
  onSubmit,
}: RenameCohortDialogProps) => {
  const canSubmit = isSubmittableCohortName(name) && !isSubmitting && !isWriteInFlight;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (canSubmit) onSubmit();
  };

  return (
    <WriteDialog
      id="rosters-admin-rename-cohort-dialog"
      open={open}
      onClose={onClose}
      title={strings.title(cohortName)}
      subtitle={strings.context(rosterName)}
      footer={
        <>
          <Button
            id="rosters-admin-rename-cohort-dialog-close"
            hierarchy="Secondary gray"
            size="md"
            onClick={onClose}
          >
            {strings.cancel}
          </Button>
          <Button
            id="rosters-admin-rename-cohort-dialog-submit"
            hierarchy="Primary"
            size="md"
            form="rosters-admin-rename-cohort-form"
            type="submit"
            disabled={!canSubmit}
          >
            {isSubmitting ? strings.submitting : strings.submit}
          </Button>
        </>
      }
    >
      <Box
        component="form"
        id="rosters-admin-rename-cohort-form"
        onSubmit={handleSubmit}
        sx={formSx}
        aria-label={strings.title(cohortName)}
      >
        <TextField
          id="rosters-admin-rename-cohort-name"
          size="small"
          label={strings.nameLabel}
          value={name}
          onChange={(event) => onNameChange(event.target.value)}
        />
      </Box>
      {isError ? (
        <Typography id="rosters-admin-rename-cohort-error" variant="text-sm-regular" role="alert">
          {noticeForError(errorMessage, strings.failed)}
        </Typography>
      ) : null}
    </WriteDialog>
  );
};

const formSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
});
